/**
 * Communications Hub Service
 * 
 * Centralized service for all communication with the external Communications Hub.
 * This service embodies the transparent software concept - every communication
 * is traceable, every decision is visible, and failures create actionable exceptions.
 * 
 * @see /docs/COMMUNICATIONS_HUB_INTEGRATION.md for full architecture documentation
 */

import { supabase } from '../integrations/supabase/client';
import type { Database } from '../integrations/supabase/types';

// =============================================================================
// EMAIL EVENT KEY MAPPING (replaces EMAIL_FUNCTIONALITY_KEYS from externalEmailService)
// =============================================================================

export const EMAIL_EVENT_KEYS = {
  ONBOARDING_INVITATION: 'onboarding.invitation.created',
  ONBOARDING_REMINDER: 'onboarding.reminder.sent',
  DELEGATION_INVITATION: 'onboarding.user_invitation.created',
  DELEGATION_REMINDER: 'onboarding.reminder.sent',
  DELEGATION_DECLINED: 'delegation.declined',
  DELEGATION_EXPIRED: 'delegation.expired',
  ESCALATION_NOTICE: 'onboarding.escalated',
  WELCOME: 'onboarding.completed',
  CREDIT_AGREEMENT_LINK: 'credit_agreement.invitation.sent',
  CREDIT_DECISION: 'credit_application.status_changed',
  SIGNATORY_INVITATION: 'signatory.invitation',
  SIGNATORY_REMINDER: 'signatory.reminder',
  AGENT_INVITATION: 'agent.invitation',
  RELATIONSHIP_ACCEPTED: 'agent.relationship_accepted',
  AI_FOLLOWUP: 'ai.followup',
  AGENT_INVITATION_MAGIC: 'agent.invitation.magic_link',
  RELATIONSHIP_ACCEPTED_NOTIFICATION: 'agent.relationship.accepted',
  SIGNING_DELEGATION_REQUEST: 'signing.delegation.request',
  SIGNING_DELEGATION_APPROVED: 'signing.delegation.approved',
  BILLING_TRANSFER_REQUEST: 'billing.transfer.request',
  RELATIONSHIP_RENEWAL_REMINDER: 'relationship.renewal.reminder',
  CUSTOM: 'email.custom',
  TEMPLATE_SEND: 'email.template.send',
} as const;

export type EmailEventKey = typeof EMAIL_EVENT_KEYS[keyof typeof EMAIL_EVENT_KEYS];

// =============================================================================
// TYPES
// =============================================================================

export type AudienceType = 'external_customer' | 'tenant_team' | 'platform_admin' | 'system_user';
export type RecipientType = 'user' | 'team' | 'contact' | 'role' | 'system';
export type Channel = 'email' | 'sms' | 'whatsapp' | 'push' | 'inbox';
export type Priority = 'critical' | 'high' | 'normal' | 'low';
export type DeliveryStatus = 'pending' | 'processing' | 'dispatched' | 'delivered' | 'failed' | 'cancelled' | 'escalated';
export type BounceType = 'hard' | 'soft';
export type SuppressionReason = 'hard_bounce' | 'complaint' | 'unsubscribe' | 'manual' | 'import';
export type FatigueLevel = 'none' | 'low' | 'medium' | 'high';

export interface Recipient {
  audienceType: AudienceType;
  recipientType: RecipientType;
  recipientRef: string;
  recipientName?: string;
  recipientAddress?: string;
  channelPreferences?: Channel[];
  context?: Record<string, any>;
}

export interface TransparencyMetadata {
  automationLogId?: string;
  triggerReason: string;
  ruleId?: string;
  ruleName?: string;
  initiatedBy: 'system' | 'user' | 'ai';
  userId?: string;
  aiGenerated?: boolean;
  aiContext?: string;
  alternativesConsidered?: Array<{
    action: string;
    reason: string;
    rejected: boolean;
  }>;
}

export interface NotificationEventPayload {
  eventKey: string;
  correlationId: string;
  sourceService?: string;
  sourceEventId?: string;
  occurredAt?: string;
  payload: {
    title: string;
    body?: string;
    contextType?: string;
    contextId?: string;
    contextUrl?: string;
    richContent?: {
      html?: string;
      markdown?: string;
      actions?: Array<{
        label: string;
        url: string;
        type?: 'primary' | 'secondary' | 'danger';
      }>;
    };
  };
  metadata?: TransparencyMetadata & Record<string, any>;
  recipients: Recipient[];
  priority?: Priority;
  channels?: Channel[];
  scheduledFor?: string;
  expiresAt?: string;
  templateId?: number;
  variables?: Record<string, any>;
}

export interface NotificationEvent {
  id: number;
  tenantId: string;
  eventKey: string;
  correlationId: string;
  status: 'pending' | 'processing' | 'processed' | 'failed' | 'skipped';
  processedAt?: string;
  errorMessage?: string;
  intentsCreated: number;
  createdAt: string;
}

export interface NotificationIntent {
  id: number;
  eventId?: number;
  audienceType: AudienceType;
  recipientType: RecipientType;
  recipientRef: string;
  recipientName?: string;
  recipientAddress?: string;
  templateId?: number;
  subject?: string;
  title?: string;
  body?: string;
  channels: Channel[];
  priority: Priority;
  status: DeliveryStatus;
  deliveryAttempts: number;
  deliveredAt?: string;
  failedAt?: string;
  errorCode?: string;
  errorMessage?: string;
  createdAt: string;
}

export interface DeliveryRecord {
  id: number;
  intentId: number;
  channel: Channel;
  provider: string;
  attemptNumber: number;
  providerMessageId?: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'bounced';
  sentAt?: string;
  deliveredAt?: string;
  failedAt?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface InboxItem {
  id: number;
  subjectType: 'user' | 'team';
  subjectRef: string;
  intentId?: number;
  title: string;
  body?: string;
  icon?: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  contextType?: string;
  contextId?: string;
  actionUrl?: string;
  actions?: Array<{
    label: string;
    url: string;
    type?: 'primary' | 'secondary' | 'danger';
  }>;
  readAt?: string;
  acknowledgedAt?: string;
  archivedAt?: string;
  createdAt: string;
}

export interface EngagementScore {
  identifier: string;
  identifierType: 'email' | 'phone';
  contactId?: number;
  score: number;
  factors: {
    recency: number;
    openRate: number;
    clickRate: number;
  };
  fatigueLevel: FatigueLevel;
  lastEngagedAt?: string;
  preferredChannel?: string;
  messagesSent30d: number;
  messagesOpened30d: number;
  messagesClicked30d: number;
}

export interface SuppressionRecord {
  identifier: string;
  identifierType: 'email' | 'phone';
  reason: SuppressionReason;
  reasonDetail?: string;
  suppressedChannels?: Channel[];
  expiresAt?: string;
  createdAt: string;
}

export interface TemplateValidation {
  valid: boolean;
  templateId: number;
  templateName: string;
  requiredVariables: string[];
  missingVariables: string[];
  unusedVariables: string[];
  errors: string[];
}

export interface WebhookTestResult {
  success: boolean;
  endpointId: number;
  endpointName: string;
  url: string;
  eventType: string;
  responseCode?: number;
  responseTime?: number;
  error?: string;
}

// =============================================================================
// CONFIGURATION
// =============================================================================

interface HubConfig {
  apiKey: string;
  tenantId: string;
}

// =============================================================================
// SERVICE IMPLEMENTATION
// =============================================================================

export class CommunicationsHubService {
  private static config: HubConfig | null = null;
  private static configCache: Map<string, HubConfig> = new Map();

  /**
   * Initialize the service with tenant-specific configuration.
   * Resolves the hub tenant ID and API key from platform_tenants.
   */
  static async initialize(tenantId: string): Promise<void> {
    if (this.configCache.has(tenantId)) {
      this.config = this.configCache.get(tenantId)!;
      return;
    }

    const { data: tenant, error } = await supabase
      .from('platform_tenants')
      .select('communications_hub_api_key, communications_hub_tenant_id')
      .eq('id', tenantId)
      .single();

    if (error || !tenant?.communications_hub_api_key) {
      this.config = {
        apiKey: import.meta.env.VITE_COMMUNICATIONS_HUB_API_KEY || '',
        tenantId: import.meta.env.VITE_COMMUNICATIONS_HUB_TENANT_ID || tenantId,
      };
    } else {
      this.config = {
        apiKey: tenant.communications_hub_api_key,
        tenantId: tenant.communications_hub_tenant_id || tenantId,
      };
    }

    this.configCache.set(tenantId, this.config);
  }

  /** Ensure config is available, auto-initializing from env if needed */
  private static async ensureConfig(): Promise<HubConfig> {
    if (this.config) return this.config;
    const fallbackTenantId = import.meta.env.VITE_COMMUNICATIONS_HUB_TENANT_ID;
    if (fallbackTenantId) {
      try { await this.initialize(fallbackTenantId); } catch { /* ignore */ }
    }
    if (!this.config) {
      throw new Error('CommunicationsHubService not initialized. Call initialize(tenantId) first.');
    }
    return this.config;
  }

  /**
   * Send a request to the hub-notifications Edge Function.
   * All notification dispatch goes through this single entry point.
   */
  private static async invokeHubNotifications<T>(
    body: any,
    options?: { idempotencyKey?: string; sandbox?: boolean }
  ): Promise<T> {
    const cfg = await this.ensureConfig();

    const headers: Record<string, string> = {
      'X-Tenant-ID': cfg.tenantId,
    };
    if (cfg.apiKey) headers['X-Hub-Api-Key'] = cfg.apiKey;
    if (options?.idempotencyKey) headers['X-Idempotency-Key'] = options.idempotencyKey;
    if (options?.sandbox) headers['X-Sandbox'] = 'true';

    const { data, error } = await supabase.functions.invoke('hub-notifications', {
      body,
      headers,
    });

    if (error) {
      throw new CommunicationsHubError(
        error.message || 'Edge Function error',
        'EDGE_FUNCTION_ERROR',
        500,
        error
      );
    }

    if (data && !data.success) {
      throw new CommunicationsHubError(
        data.error?.message || 'Request failed',
        data.error?.code || 'UNKNOWN_ERROR',
        400,
        data
      );
    }

    return data as T;
  }

  // ===========================================================================
  // NOTIFICATION EVENTS — dispatched via hub-notifications Edge Function
  // ===========================================================================

  /**
   * Send a notification event via the hub-notifications Edge Function.
   * This is the primary method for all outbound communications.
   */
  static async sendNotificationEvent(
    event: NotificationEventPayload,
    options?: { idempotencyKey?: string; sandbox?: boolean }
  ): Promise<{ success: boolean; data: NotificationEvent }> {
    const payload = {
      ...event,
      sourceService: event.sourceService || 'fresh-start-onboarding',
      occurredAt: event.occurredAt || new Date().toISOString()
    };

    return this.invokeHubNotifications(payload, options);
  }

  /**
   * Send multiple notification events in a single batch.
   * Dispatches each event sequentially through the Edge Function.
   */
  static async sendNotificationEventsBatch(
    events: NotificationEventPayload[],
    options?: { sandbox?: boolean }
  ): Promise<{ success: boolean; data: { queued: number; failed: number; errors?: string[] } }> {
    let queued = 0;
    let failed = 0;
    const errors: string[] = [];
    for (const event of events) {
      try {
        await this.sendNotificationEvent(event, options);
        queued++;
      } catch (e: any) {
        failed++;
        errors.push(e.message || 'Unknown error');
      }
    }
    return { success: failed === 0, data: { queued, failed, errors: errors.length ? errors : undefined } };
  }

  /**
   * Get a notification event by ID — queries hub_message_logs directly
   */
  static async getNotificationEvent(eventId: number): Promise<{ success: boolean; data: NotificationEvent }> {
    const { data, error } = await supabase
      .from('hub_message_logs')
      .select('*')
      .eq('id', eventId)
      .single();
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    return { success: true, data: data as any };
  }

  /**
   * Replay a notification event (re-send via Edge Function)
   */
  static async replayNotificationEvent(eventId: number): Promise<{ success: boolean; data: NotificationEvent }> {
    const original = await this.getNotificationEvent(eventId);
    return this.sendNotificationEvent(original.data as any);
  }

  /**
   * Simulate a notification event without actually sending
   */
  static async simulateNotificationEvent(
    event: NotificationEventPayload
  ): Promise<{ success: boolean; data: { wouldSend: boolean; channels: Channel[]; recipients: any[] } }> {
    return {
      success: true,
      data: {
        wouldSend: true,
        channels: (event as any).channels || ['email'],
        recipients: event.recipients || [],
      }
    };
  }

  // ===========================================================================
  // NOTIFICATION INTENTS — query hub_message_logs directly
  // ===========================================================================

  /**
   * Get notification intents (delivery status) for an event
   */
  static async getIntentsForEvent(eventId: number): Promise<{ success: boolean; data: NotificationIntent[] }> {
    const { data, error } = await supabase
      .from('hub_message_logs')
      .select('*')
      .eq('id', eventId);
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    return { success: true, data: (data || []) as any };
  }

  /**
   * Get a specific intent by ID
   */
  static async getIntent(intentId: number): Promise<{ success: boolean; data: NotificationIntent }> {
    const { data, error } = await supabase
      .from('hub_message_logs')
      .select('*')
      .eq('id', intentId)
      .single();
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    return { success: true, data: data as any };
  }

  /**
   * Get delivery timeline for an intent
   */
  static async getIntentTimeline(intentId: number): Promise<{ success: boolean; data: DeliveryRecord[] }> {
    return { success: true, data: [] };
  }

  /**
   * Cancel a pending notification intent
   */
  static async cancelIntent(intentId: number): Promise<{ success: boolean }> {
    const { error } = await supabase
      .from('hub_message_logs')
      .update({ status: 'cancelled' })
      .eq('id', intentId)
      .eq('status', 'pending');
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    return { success: true };
  }

  // ===========================================================================
  // IN-APP INBOX — query in_app_notifications directly
  // ===========================================================================

  /**
   * Get inbox notifications for a user
   */
  static async getInbox(
    subjectType: 'user' | 'team',
    subjectRef: string,
    options?: { unreadOnly?: boolean; limit?: number; offset?: number }
  ): Promise<{ success: boolean; data: { items: InboxItem[]; unreadCount: number } }> {
    let query = supabase
      .from('in_app_notifications')
      .select('*')
      .eq('user_id', subjectRef)
      .order('created_at', { ascending: false })
      .limit(options?.limit || 50);

    if (options?.unreadOnly) query = query.eq('is_read', false);
    if (options?.offset) query = query.range(options.offset, options.offset + (options.limit || 50) - 1);

    const { data, error } = await query;
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);

    const items = (data || []) as any as InboxItem[];
    const unreadCount = items.filter((i: any) => !i.is_read).length;
    return { success: true, data: { items, unreadCount } };
  }

  /**
   * Mark an inbox item as read
   */
  static async markInboxItemRead(itemId: number): Promise<{ success: boolean }> {
    const { error } = await supabase
      .from('in_app_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', itemId);
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    return { success: true };
  }

  /**
   * Mark multiple inbox items as read
   */
  static async markInboxItemsRead(itemIds: number[]): Promise<{ success: boolean; data: { updated: number } }> {
    const { error, count } = await supabase
      .from('in_app_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .in('id', itemIds);
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    return { success: true, data: { updated: count || itemIds.length } };
  }

  /**
   * Acknowledge an inbox item (mark as actioned)
   */
  static async acknowledgeInboxItem(itemId: number): Promise<{ success: boolean }> {
    const { error } = await supabase
      .from('in_app_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', itemId);
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    return { success: true };
  }

  /**
   * Archive an inbox item
   */
  static async archiveInboxItem(itemId: number): Promise<{ success: boolean }> {
    const { error } = await supabase
      .from('in_app_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', itemId);
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    return { success: true };
  }

  // ===========================================================================
  // RECIPIENT MANAGEMENT — stubs (no backing table yet)
  // ===========================================================================

  /**
   * Get engagement score for a recipient
   */
  static async getEngagementScore(identifier: string): Promise<{ success: boolean; data: EngagementScore }> {
    return { success: true, data: { contactId: null, score: 50, fatigueLevel: 'none' } as any };
  }

  /**
   * Add a recipient to the suppression list
   */
  static async suppressRecipient(params: {
    email?: string;
    phone?: string;
    reason: SuppressionReason;
    reasonDetail?: string;
    suppressedChannels?: Channel[];
    expiresAt?: string;
  }): Promise<{ success: boolean }> {
    console.warn('[CommunicationsHubService] suppressRecipient not yet backed by a table');
    return { success: true };
  }

  /**
   * Check if a recipient is suppressed
   */
  static async checkSuppression(identifier: string): Promise<{ success: boolean; data: { suppressed: boolean } & Partial<SuppressionRecord> }> {
    return { success: true, data: { suppressed: false } };
  }

  /**
   * Remove a recipient from the suppression list
   */
  static async removeSuppression(identifier: string): Promise<{ success: boolean }> {
    return { success: true };
  }

  /**
   * List all suppressed recipients
   */
  static async listSuppressions(options?: {
    limit?: number;
    offset?: number;
    reason?: SuppressionReason;
  }): Promise<{ success: boolean; data: SuppressionRecord[] }> {
    return { success: true, data: [] };
  }

  // ===========================================================================
  // TEMPLATES — query hub_templates directly
  // ===========================================================================

  /**
   * Validate template variables before sending
   */
  static async validateTemplate(
    templateId: number,
    variables: Record<string, any>
  ): Promise<{ success: boolean; data: TemplateValidation }> {
    const { data: tpl, error } = await supabase
      .from('hub_templates')
      .select('variables')
      .eq('id', templateId)
      .single();
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    const required = Object.keys(tpl?.variables || {});
    const missing = required.filter(k => !(k in variables));
    return { success: true, data: { valid: missing.length === 0, missingVariables: missing } as any };
  }

  /**
   * Render a template to HTML (useful for previews)
   */
  static async renderTemplate(
    templateId: number,
    variables: Record<string, any>
  ): Promise<{ success: boolean; data: { html: string; renderedAt: string } }> {
    const { data: tpl, error } = await supabase
      .from('hub_templates')
      .select('html_content')
      .eq('id', templateId)
      .single();
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    let html = tpl?.html_content || '';
    for (const [key, val] of Object.entries(variables)) {
      html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(val ?? ''));
    }
    return { success: true, data: { html, renderedAt: new Date().toISOString() } };
  }

  /**
   * List available templates
   */
  static async listTemplates(options?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: any[] }> {
    const limit = options?.limit || 50;
    const offset = ((options?.page || 1) - 1) * limit;
    let query = supabase
      .from('hub_templates')
      .select('*')
      .order('updated_at', { ascending: false })
      .range(offset, offset + limit - 1);
    if (options?.search) query = query.ilike('name', `%${options.search}%`);
    const { data, error } = await query;
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    return { success: true, data: data || [] };
  }

  // ===========================================================================
  // WEBHOOKS — stubs (no backing table yet)
  // ===========================================================================

  /**
   * Test a webhook endpoint
   */
  static async testWebhookEndpoint(
    endpointId: number,
    eventType?: string
  ): Promise<{ success: boolean; data: WebhookTestResult }> {
    return { success: true, data: { delivered: false, statusCode: 0, responseTime: 0 } as any };
  }

  /**
   * List configured webhook endpoints
   */
  static async listWebhookEndpoints(): Promise<{ success: boolean; data: any[] }> {
    return { success: true, data: [] };
  }

  // ===========================================================================
  // PREFERENCES — stubs (no backing table yet)
  // ===========================================================================

  /**
   * Get notification preferences for a subject
   */
  static async getPreferences(
    subjectType: 'user' | 'team' | 'tenant' | 'contact',
    subjectRef: string
  ): Promise<{ success: boolean; data: any[] }> {
    return { success: true, data: [] };
  }

  /**
   * Update channel preference for a subject
   */
  static async updateChannelPreference(
    subjectType: 'user' | 'team' | 'tenant' | 'contact',
    subjectRef: string,
    channel: Channel,
    settings: {
      deliveryMode?: 'immediate' | 'digest' | 'scheduled' | 'muted';
      digestFrequency?: 'hourly' | 'daily' | 'weekly';
      isEnabled?: boolean;
    }
  ): Promise<{ success: boolean }> {
    console.warn('[CommunicationsHubService] updateChannelPreference not yet backed by a table');
    return { success: true };
  }

  // ===========================================================================
  // ANALYTICS — query hub_message_logs directly
  // ===========================================================================

  /**
   * Get analytics overview
   */
  static async getAnalyticsOverview(options?: {
    startDate?: string;
    endDate?: string;
  }): Promise<{ success: boolean; data: any }> {
    let query = supabase
      .from('hub_message_logs')
      .select('status, channel', { count: 'exact' });
    if (options?.startDate) query = query.gte('created_at', options.startDate);
    if (options?.endDate) query = query.lte('created_at', options.endDate);
    const { data, count, error } = await query;
    if (error) throw new CommunicationsHubError(error.message, 'DB_ERROR', 500, error);
    const sent = (data || []).filter((r: any) => r.status === 'sent').length;
    const failed = (data || []).filter((r: any) => r.status === 'failed').length;
    return { success: true, data: { total: count || 0, sent, failed, deliveryRate: count ? sent / count : 0 } };
  }

  // ===========================================================================
  // HELPER METHODS
  // ===========================================================================

  /**
   * Determine optimal channels based on engagement score
   */
  static async selectOptimalChannels(
    recipientEmail: string,
    defaultChannels: Channel[] = ['email']
  ): Promise<Channel[]> {
    try {
      const engagement = await this.getEngagementScore(recipientEmail);
      
      if (!engagement.data.contactId) {
        // New contact - use defaults
        return defaultChannels;
      }

      const { score, fatigueLevel } = engagement.data;

      // High engagement, low fatigue - email only
      if (score >= 70 && fatigueLevel === 'none') {
        return ['email'];
      }

      // Medium engagement or some fatigue - multi-channel
      if (score >= 40 || fatigueLevel === 'low') {
        return ['email', 'sms'];
      }

      // High fatigue - avoid email
      if (fatigueLevel === 'high') {
        return ['sms', 'inbox'];
      }

      // Low engagement - aggressive multi-channel
      return ['email', 'sms', 'whatsapp'];
    } catch {
      // On error, use defaults
      return defaultChannels;
    }
  }

  /**
   * Build a standard onboarding invitation event
   */
  static buildOnboardingInvitationEvent(params: {
    flowId: string;
    roleId: string;
    recipientEmail: string;
    recipientName: string;
    organizationName: string;
    tenantName: string;
    actionUrl: string;
    expiresAt: string;
    triggerReason: string;
    initiatedBy: 'system' | 'user';
    userId?: string;
    channels?: Channel[];
  }): NotificationEventPayload {
    return {
      eventKey: 'onboarding.invitation.created',
      correlationId: `flow_${params.flowId}_role_${params.roleId}`,
      sourceService: 'fresh-start-onboarding',
      payload: {
        title: 'Welcome to Onboarding',
        body: `You've been invited to complete onboarding for ${params.organizationName}`,
        contextType: 'onboarding_flow',
        contextId: params.flowId,
        contextUrl: params.actionUrl,
        richContent: {
          actions: [
            {
              label: 'Start Onboarding',
              url: params.actionUrl,
              type: 'primary'
            }
          ]
        }
      },
      metadata: {
        triggerReason: params.triggerReason,
        initiatedBy: params.initiatedBy,
        userId: params.userId
      },
      recipients: [{
        audienceType: 'external_customer',
        recipientType: 'contact',
        recipientRef: params.recipientEmail,
        recipientName: params.recipientName,
        recipientAddress: params.recipientEmail,
        channelPreferences: params.channels || ['email']
      }],
      priority: 'high',
      expiresAt: params.expiresAt,
      variables: {
        recipient: {
          name: params.recipientName,
          email: params.recipientEmail
        },
        organization: {
          name: params.organizationName
        },
        tenant: {
          name: params.tenantName
        },
        action: {
          url: params.actionUrl,
          label: 'Start Onboarding',
          expiresAt: params.expiresAt
        }
      }
    };
  }

  /**
   * Build a custom message event with AI-generated or context-specific wording
   * 
   * HYBRID MODEL:
   * - Fresh-Start controls: subject, message body (wording)
   * - Hub controls: HTML wrapper, styling, branding, delivery
   * 
   * The Hub will take the subject/body and wrap it in the appropriate
   * HTML template based on the eventKey (e.g., 'onboarding.custom_message')
   */
  static buildCustomMessageEvent(params: {
    eventKey: string;
    correlationId: string;
    // Message content (Fresh-Start/AI controlled)
    subject: string;
    messageBody: string;
    // Optional call-to-action
    actionUrl?: string;
    actionLabel?: string;
    // Recipient info
    recipientEmail: string;
    recipientName: string;
    // Context
    organizationName?: string;
    flowId?: string;
    roleId?: string;
    // Transparency
    triggerReason: string;
    initiatedBy: 'system' | 'user' | 'ai';
    aiContext?: string;
    // Delivery options
    channels?: Channel[];
    priority?: Priority;
  }): NotificationEventPayload {
    return {
      eventKey: params.eventKey,
      correlationId: params.correlationId,
      sourceService: 'fresh-start-onboarding',
      payload: {
        // Fresh-Start controls the wording
        title: params.subject,
        body: params.messageBody,
        contextType: params.flowId ? 'onboarding_flow' : undefined,
        contextId: params.flowId,
        contextUrl: params.actionUrl,
        richContent: params.actionUrl ? {
          actions: [{
            label: params.actionLabel || 'Take Action',
            url: params.actionUrl,
            type: 'primary'
          }]
        } : undefined
      },
      metadata: {
        triggerReason: params.triggerReason,
        initiatedBy: params.initiatedBy,
        aiGenerated: params.initiatedBy === 'ai',
        aiContext: params.aiContext,
        flowId: params.flowId,
        roleId: params.roleId
      },
      recipients: [{
        audienceType: 'external_customer',
        recipientType: 'contact',
        recipientRef: params.recipientEmail,
        recipientName: params.recipientName,
        recipientAddress: params.recipientEmail,
        channelPreferences: params.channels || ['email']
      }],
      priority: params.priority || 'normal',
      // Variables for Hub template placeholders
      variables: {
        recipient: {
          name: params.recipientName,
          email: params.recipientEmail
        },
        organization: params.organizationName ? {
          name: params.organizationName
        } : undefined,
        // The Hub can use these to populate its HTML wrapper
        content: {
          subject: params.subject,
          body: params.messageBody,
          actionUrl: params.actionUrl,
          actionLabel: params.actionLabel
        }
      }
    };
  }

  /**
   * Build an AI-personalized message event
   * Use when AI has generated custom wording based on context
   */
  static buildAIPersonalizedEvent(params: {
    baseEventKey: string;
    flowId: string;
    roleId?: string;
    // AI-generated content
    aiSubject: string;
    aiMessageBody: string;
    aiRationale: string;
    // Recipient
    recipientEmail: string;
    recipientName: string;
    organizationName: string;
    // Action
    actionUrl: string;
    actionLabel?: string;
    // Options
    channels?: Channel[];
  }): NotificationEventPayload {
    return this.buildCustomMessageEvent({
      eventKey: `${params.baseEventKey}.ai_personalized`,
      correlationId: `flow_${params.flowId}${params.roleId ? `_role_${params.roleId}` : ''}_ai_${Date.now()}`,
      subject: params.aiSubject,
      messageBody: params.aiMessageBody,
      actionUrl: params.actionUrl,
      actionLabel: params.actionLabel,
      recipientEmail: params.recipientEmail,
      recipientName: params.recipientName,
      organizationName: params.organizationName,
      flowId: params.flowId,
      roleId: params.roleId,
      triggerReason: `AI-personalized message: ${params.aiRationale}`,
      initiatedBy: 'ai',
      aiContext: params.aiRationale,
      channels: params.channels
    });
  }

  // ===========================================================================
  // EMAIL FACADE (Phase 3 - consolidation layer)
  // ===========================================================================

  /**
   * Send an email through the Communications Hub.
   * This is the unified email method that replaces ExternalEmailService and EmailService.
   * 
   * Maps legacy functionality keys to Hub event keys and routes through
   * sendNotificationEvent with channels: ['email'].
   */
  static async sendEmail(params: {
    eventKey: string;
    recipientEmail: string;
    recipientName?: string;
    subject?: string;
    variables?: Record<string, any>;
    tenantId?: string;
    correlationId?: string;
    onboardingFlowId?: string;
    clientId?: string;
    priority?: Priority;
    templateId?: number;
    /** For DB-template sends: raw body content */
    body?: string;
    metadata?: Partial<TransparencyMetadata> & Record<string, any>;
  }): Promise<{ success: boolean; messageId?: string; error?: string; provider?: string }> {
    try {
      // Ensure service is initialized with the correct tenant context
      if (params.tenantId) {
        await this.initialize(params.tenantId);
      }

      const result = await this.sendNotificationEvent({
        eventKey: params.eventKey,
        correlationId: params.correlationId || `email_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        sourceService: 'fresh-start-onboarding',
        payload: {
          title: params.subject || '',
          body: params.body || '',
          contextType: params.onboardingFlowId ? 'onboarding_flow' : params.clientId ? 'client' : undefined,
          contextId: params.onboardingFlowId || params.clientId,
        },
        recipients: [{
          audienceType: 'external_customer' as AudienceType,
          recipientType: 'contact' as RecipientType,
          recipientRef: params.recipientEmail,
          recipientName: params.recipientName,
          recipientAddress: params.recipientEmail,
          channelPreferences: ['email' as Channel],
        }],
        channels: ['email' as Channel],
        priority: params.priority || 'normal',
        templateId: params.templateId,
        variables: params.variables,
        metadata: {
          triggerReason: params.metadata?.triggerReason || `Email: ${params.eventKey}`,
          initiatedBy: params.metadata?.initiatedBy || 'system',
          ...params.metadata,
        },
      });

      return {
        success: result.success,
        messageId: result.data?.id?.toString(),
      };
    } catch (error) {
      console.error('❌ CommunicationsHub sendEmail error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Send email using a database template (from Tenant Settings).
   * Replaces EmailService.sendWithTemplate().
   */
  static async sendEmailWithTemplate(params: {
    templateId: string;
    recipientEmail: string;
    recipientName?: string;
    variables: Record<string, any>;
    onboardingFlowId?: string;
    clientId?: string;
    tenantId?: string;
  }): Promise<{ success: boolean; messageId?: string; error?: string }> {
    return this.sendEmail({
      eventKey: 'email.template.send',
      recipientEmail: params.recipientEmail,
      recipientName: params.recipientName,
      variables: { ...params.variables, templateId: params.templateId },
      onboardingFlowId: params.onboardingFlowId,
      clientId: params.clientId,
      tenantId: params.tenantId,
    });
  }

  /**
   * Get available email templates for a tenant by category.
   * Replaces EmailService.getTemplatesByCategory().
   */
  static async getEmailTemplatesByCategory(
    tenantId: string,
    category?: string
  ): Promise<Array<{
    id: string; name: string; subject: string; body: string;
    body_html?: string; category: string; variables?: Record<string, any>;
    platform_tenant_id?: string;
  }>> {
    // Resolve hub tenant_id from platform_tenant uuid
    let hubTenantId: string | null = null;
    if (tenantId) {
      const { data: pt } = await supabase
        .from('platform_tenants')
        .select('communications_hub_tenant_id')
        .eq('id', tenantId)
        .maybeSingle();
      hubTenantId = (pt as any)?.communications_hub_tenant_id || null;
    }

    let query = (supabase as any)
      .from('hub_templates')
      .select('id, name, subject, html_content, text_content, category, variables')
      .eq('status', 'active');

    if (hubTenantId) {
      query = query.eq('tenant_id', hubTenantId);
    }
    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query.order('name');
    if (error) {
      console.error('Failed to fetch hub templates:', error);
      return [];
    }

    // Normalise to the expected shape (body = html_content)
    return (data || []).map((t: any) => ({
      id: String(t.id),
      name: t.name,
      subject: t.subject || '',
      body: t.html_content || t.text_content || '',
      body_html: t.html_content || undefined,
      category: t.category,
      variables: t.variables || {},
    }));
  }

  /**
   * Get email history for a contact.
   * Replaces EmailService.getEmailHistory().
   */
  static async getEmailHistory(params: {
    recipientEmail?: string;
    onboardingFlowId?: string;
    clientId?: string;
    limit?: number;
  }): Promise<Array<{
    id: string; subject: string; status: string; sent_at: string;
    opened_at?: string; bounced_at?: string; hub_template_id?: number;
  }>> {
    let query = supabase
      .from('email_logs')
      .select('id, subject, status, sent_at, opened_at, bounced_at, hub_template_id, recipient_email')
      .order('sent_at', { ascending: false })
      .limit(params.limit || 50);

    if (params.recipientEmail) query = query.eq('recipient_email', params.recipientEmail);
    if (params.onboardingFlowId) query = query.eq('onboarding_flow_id', params.onboardingFlowId);
    if (params.clientId) query = query.eq('client_id', params.clientId);

    const { data, error } = await query;
    if (error) {
      console.error('Failed to fetch email history:', error);
      return [];
    }
    return (data || []).map(item => ({
      id: item.id, subject: item.subject, status: item.status, sent_at: item.sent_at,
      opened_at: item.opened_at || undefined, bounced_at: item.bounced_at || undefined,
      hub_template_id: item.hub_template_id || undefined,
    }));
  }

  /**
   * Build a reminder notification event
   */
  static buildReminderEvent(params: {
    flowId: string;
    roleId: string;
    reminderNumber: number;
    recipientEmail: string;
    recipientName: string;
    organizationName: string;
    actionUrl: string;
    daysSinceInvitation: number;
    channels?: Channel[];
  }): NotificationEventPayload {
    return {
      eventKey: `onboarding.reminder.scheduled`,
      correlationId: `flow_${params.flowId}_role_${params.roleId}_reminder_${params.reminderNumber}`,
      sourceService: 'fresh-start-onboarding',
      payload: {
        title: 'Reminder: Complete Your Onboarding',
        body: `Your onboarding for ${params.organizationName} is still pending`,
        contextType: 'onboarding_flow',
        contextId: params.flowId,
        contextUrl: params.actionUrl
      },
      metadata: {
        triggerReason: `No activity detected for ${params.daysSinceInvitation} days after invitation`,
        ruleName: 'reminder_schedule',
        initiatedBy: 'system'
      },
      recipients: [{
        audienceType: 'external_customer',
        recipientType: 'contact',
        recipientRef: params.recipientEmail,
        recipientName: params.recipientName,
        recipientAddress: params.recipientEmail,
        channelPreferences: params.channels || ['email', 'sms']
      }],
      priority: params.reminderNumber >= 3 ? 'high' : 'normal',
      variables: {
        recipient: {
          name: params.recipientName,
          email: params.recipientEmail
        },
        organization: {
          name: params.organizationName
        },
        action: {
          url: params.actionUrl,
          label: 'Complete Onboarding'
        },
        context: {
          reminderNumber: params.reminderNumber,
          daysSinceInvitation: params.daysSinceInvitation
        }
      }
    };
  }
}

// =============================================================================
// ERROR CLASS
// =============================================================================

export class CommunicationsHubError extends Error {
  public code: string;
  public statusCode: number;
  public details?: unknown;
  
  constructor(
    message: string,
    code: string,
    statusCode: number,
    details?: unknown
  ) {
    super(message);
    this.name = 'CommunicationsHubError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }
}
